import { useState } from "react";
import { Button } from "./ui/button";
import { weekDays, mondayOf, addDays, dayNumber, shortMonth, todayISO } from "../lib/dates";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, PawPrint, Ban, RotateCcw } from "lucide-react";

const WD_SHORT = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MONTHS_FULL = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function monthLabel(iso) {
  const [y, m] = iso.split("-").map(Number);
  return `${MONTHS_FULL[m - 1]} ${y}`;
}

function monthMatrix(iso) {
  const [y, m] = iso.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstIso = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastIso = `${y}-${String(m).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;
  const start = mondayOf(firstIso);
  const end = addDays(mondayOf(lastIso), 6);
  const weeks = [];
  let cur = start;
  let guard = 0;
  while (cur <= end && guard < 8) {
    const row = [];
    for (let d = 0; d < 7; d++) { row.push(cur); cur = addDays(cur, 1); }
    weeks.push(row);
    guard += 1;
  }
  return { weeks, month: m };
}

export function AgendaCalendar({ bookings, slots, blockedDays = [], onBlock, onUnblock }) {
  const [view, setView] = useState("week");
  const [monday, setMonday] = useState(mondayOf(todayISO()));
  const [monthCursor, setMonthCursor] = useState(todayISO().slice(0, 7) + "-01");
  const today = todayISO();
  const blockedSet = new Set(blockedDays);

  const active = bookings.filter((b) => b.status !== "cancelled");
  const bookingsByDay = {};
  active.forEach((b) => { (bookingsByDay[b.slot_date] = bookingsByDay[b.slot_date] || []).push(b); });
  const slotsByDay = {};
  (slots || []).forEach((s) => { (slotsByDay[s.date] = slotsByDay[s.date] || []).push(s); });

  const ViewToggle = () => (
    <div className="inline-flex bg-slate-100 rounded-xl p-1" data-testid="agenda-view-toggle">
      <button onClick={() => setView("week")} data-testid="agenda-view-week" className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${view === "week" ? "bg-white text-[#373E45] shadow-sm" : "text-[#64748B]"}`}>Semana</button>
      <button onClick={() => setView("month")} data-testid="agenda-view-month" className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${view === "month" ? "bg-white text-[#373E45] shadow-sm" : "text-[#64748B]"}`}>Mes</button>
    </div>
  );

  // ---------- WEEK ----------
  const days = weekDays(monday);
  const rangeLabel = `${dayNumber(days[0])} ${shortMonth(days[0])} – ${dayNumber(days[6])} ${shortMonth(days[6])}`;
  const weekCount = active.filter((b) => days.includes(b.slot_date)).length;

  // ---------- MONTH ----------
  const { weeks, month } = monthMatrix(monthCursor);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid="agenda-calendar">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-bold text-[#373E45] flex items-center gap-2 text-lg capitalize">
            <CalendarDays className="h-5 w-5 text-[#7FD498]" />
            {view === "week" ? `Semana del ${rangeLabel}` : monthLabel(monthCursor)}
          </h3>
          {view === "week" && <p className="text-sm text-[#64748B] mt-0.5">{weekCount} {weekCount === 1 ? "cita agendada" : "citas agendadas"} esta semana</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ViewToggle />
          {view === "week" ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setMonday(addDays(monday, -7))} className="rounded-xl border-slate-300" data-testid="agenda-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setMonday(mondayOf(today))} className="rounded-xl border-slate-300 font-semibold text-[#373E45]" data-testid="agenda-today">Hoy</Button>
              <Button variant="outline" size="sm" onClick={() => setMonday(addDays(monday, 7))} className="rounded-xl border-slate-300" data-testid="agenda-next-week"><ChevronRight className="h-4 w-4" /></Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setMonthCursor(shiftMonth(monthCursor, -1))} className="rounded-xl border-slate-300" data-testid="agenda-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setMonthCursor(today.slice(0, 7) + "-01")} className="rounded-xl border-slate-300 font-semibold text-[#373E45]" data-testid="agenda-this-month">Hoy</Button>
              <Button variant="outline" size="sm" onClick={() => setMonthCursor(shiftMonth(monthCursor, 1))} className="rounded-xl border-slate-300" data-testid="agenda-next-month"><ChevronRight className="h-4 w-4" /></Button>
            </>
          )}
        </div>
      </div>

      {view === "week" ? (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {days.map((d, i) => {
            const dayB = (bookingsByDay[d] || []).sort((a, b) => a.slot_start.localeCompare(b.slot_start));
            const freeCount = (slotsByDay[d] || []).length;
            const isToday = d === today;
            const isBlocked = blockedSet.has(d);
            const isPast = d < today;
            return (
              <div key={d} className={`rounded-xl border p-3 min-h-[150px] flex flex-col ${isBlocked ? "border-red-200 bg-red-50/60" : isToday ? "border-[#7FD498] bg-[#7FD498]/5" : "border-slate-200 bg-slate-50/60"}`} data-testid={`agenda-day-${d}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-xs font-bold text-[#64748B] uppercase">{WD_SHORT[i]}</p>
                    <p className="text-lg font-extrabold leading-none text-[#373E45]">{dayNumber(d)}</p>
                  </div>
                  {isBlocked ? <span className="text-[10px] font-bold text-red-600 bg-red-100 rounded-full px-2 py-0.5">Bloqueado</span>
                    : freeCount > 0 ? <span className="text-[10px] font-bold text-[#2f7a45] bg-[#7FD498]/20 rounded-full px-2 py-0.5">{freeCount} libres</span> : null}
                </div>
                <div className="space-y-1.5 flex-1">
                  {isBlocked ? <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><Ban className="h-3 w-3" />Día no disponible</p>
                    : dayB.length === 0 ? <p className="text-xs text-slate-400 mt-2">Sin citas</p>
                    : dayB.map((b) => (
                      <div key={b.id} className={`rounded-lg px-2.5 py-2 border ${b.status === "confirmed" ? "bg-[#7FD498]/15 border-[#7FD498]/50" : "bg-[#FBBC0C]/15 border-[#FBBC0C]/50"}`} data-testid={`agenda-appt-${b.id}`}>
                        <div className="flex items-center gap-1 text-xs font-bold text-[#373E45]"><Clock className="h-3 w-3" />{b.slot_start}</div>
                        <p className="text-xs font-semibold text-[#373E45] mt-0.5 flex items-center gap-1"><PawPrint className="h-3 w-3 text-[#64748B]" />{b.patient_name}</p>
                        <p className="text-[11px] text-[#64748B] truncate">{b.clinic_name}</p>
                        <p className="text-[11px] text-[#64748B] truncate">{b.study_type}</p>
                      </div>
                    ))}
                </div>
                {!isPast && (isBlocked ? (
                  <button onClick={() => onUnblock?.(d)} data-testid={`unblock-day-${d}`} className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[11px] font-bold text-[#2f7a45] bg-[#7FD498]/15 hover:bg-[#7FD498]/30 rounded-lg py-1.5 transition-colors"><RotateCcw className="h-3 w-3" />Desbloquear</button>
                ) : (
                  <button onClick={() => onBlock?.(d)} data-testid={`block-day-${d}`} className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[11px] font-bold text-red-500 bg-red-50 hover:bg-red-100 rounded-lg py-1.5 transition-colors"><Ban className="h-3 w-3" />Bloquear día</button>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div data-testid="agenda-month">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {WD_SHORT.map((w) => <p key={w} className="text-xs font-bold text-[#64748B] uppercase text-center">{w}</p>)}
          </div>
          <div className="space-y-2">
            {weeks.map((row, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-2">
                {row.map((d) => {
                  const inMonth = Number(d.split("-")[1]) === month;
                  const dayB = (bookingsByDay[d] || []);
                  const freeCount = (slotsByDay[d] || []).length;
                  const isToday = d === today;
                  const isBlocked = blockedSet.has(d);
                  const isPast = d < today;
                  return (
                    <div key={d} className={`group relative rounded-lg border p-2 min-h-[84px] ${!inMonth ? "opacity-40" : ""} ${isBlocked ? "border-red-200 bg-red-50/60" : isToday ? "border-[#7FD498] bg-[#7FD498]/5" : "border-slate-200 bg-slate-50/50"}`} data-testid={`agenda-mday-${d}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-bold ${isToday ? "text-[#2f7a45]" : "text-[#373E45]"}`}>{dayNumber(d)}</span>
                        {!isPast && inMonth && (isBlocked ? (
                          <button onClick={() => onUnblock?.(d)} data-testid={`unblock-mday-${d}`} title="Desbloquear" className="opacity-0 group-hover:opacity-100 transition-opacity text-[#2f7a45]"><RotateCcw className="h-3.5 w-3.5" /></button>
                        ) : (
                          <button onClick={() => onBlock?.(d)} data-testid={`block-mday-${d}`} title="Bloquear día" className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500"><Ban className="h-3.5 w-3.5" /></button>
                        ))}
                      </div>
                      {isBlocked ? (
                        <p className="text-[10px] font-bold text-red-500 mt-1">Bloqueado</p>
                      ) : (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {dayB.slice(0, 4).map((b) => <span key={b.id} className={`h-2 w-2 rounded-full ${b.status === "confirmed" ? "bg-[#7FD498]" : "bg-[#FBBC0C]"}`} title={`${b.slot_start} ${b.patient_name}`} />)}
                          {dayB.length > 4 && <span className="text-[9px] font-bold text-[#64748B]">+{dayB.length - 4}</span>}
                        </div>
                      )}
                      {!isBlocked && dayB.length > 0 && <p className="text-[10px] font-semibold text-[#373E45] mt-1">{dayB.length} {dayB.length === 1 ? "cita" : "citas"}</p>}
                      {!isBlocked && dayB.length === 0 && freeCount > 0 && <p className="text-[10px] text-[#2f7a45] mt-1">{freeCount} libres</p>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-[#64748B]">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#7FD498]" />Confirmada</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#FBBC0C]" />Pendiente</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#7FD498]/20 border border-[#7FD498]" />Franjas libres</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-100 border border-red-300" />Bloqueado</span>
      </div>
    </div>
  );
}

function shiftMonth(iso, delta) {
  let [y, m] = iso.split("-").map(Number);
  m += delta;
  while (m < 1) { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return `${y}-${String(m).padStart(2, "0")}-01`;
}
